// src/pages/fleetChecksx
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useCommands } from '../api/commands';
import PageHeader from '../components/PageHeader';

// ---- Import schema objects (no components). Keep the  extension if your files are 
import vehicleCheckGPM   from '../forms/vehicleCheckGPM';
import asbSolo           from '../forms/asbSolo';
import cleaningCover     from '../forms/cleaningCover';
import gardeningVan      from '../forms/gardeningVan';
import mixed             from '../forms/mixed';
import carpetCleaning    from '../forms/carpetCleaning';

// Map of all available check schemas
export const FLEET_CHECK_SCHEMAS = {
  vehicleCheckGPM,
  asbSolo,
  cleaningCover,
  gardeningVan,
  mixed,
  carpetCleaning,
};

// Human labels for selector
const FORM_OPTIONS = [
  { value: 'vehicleCheckGPM', label: 'Standard Vehicle Check' },
  { value: 'asbSolo',         label: 'ASB — Solo Van Check' },
  { value: 'cleaningCover',   label: 'Cleaning Cover — Van Check' },
  { value: 'gardeningVan',    label: 'Gardening — Van Check' },
  { value: 'mixed',           label: 'Mixed Department Check' },
  { value: 'carpetCleaning',  label: 'Carpet Cleaning — Van Check' },
];

// Utility: find which field is “mileage”
function detectMileageKey(schema) {
  const explicit = schema.fields.find(f =>
    String(f.type || '').toLowerCase() === 'mileage'
  )?.key;
  if (explicit) return explicit;

  const hit = schema.fields.find(f => {
    const k = (f.key || '').toLowerCase();
    const l = (f.label || '').toLowerCase();
    return (
      k === 'mileage' ||
      k === 'odometer' ||
      l.includes('mileage') ||
      l.includes('odometer')
    );
  });
  return hit?.key || null;
}

// Normalize a field spec to know how to render it
function fieldInputType(f) {
  const t = String(f.type || 'text').toLowerCase();
  if (['text', 'string'].includes(t)) return 'text';
  if (['number', 'numeric'].includes(t)) return 'number';
  if (['date', 'datetime'].includes(t)) return 'date';
  if (['textarea', 'multiline'].includes(t)) return 'textarea';
  if (['select', 'enum'].includes(t)) return 'select';
  return 'text';
}

// File -> data URI
function fileToDataUri(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve({ name: file.name, dataUri: String(fr.result || '') });
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

// find plate/registration field by key or label
function detectPlateKey(schema){
  const hit = (schema.fields || []).find(f => {
    const k = (f.key || '').toLowerCase();
    const l = (f.label || '').toLowerCase();
    return (
      k === 'reg' ||
      k === 'plate' ||
      k === 'numberplate' ||
      k === 'vehiclenumberplate' ||
      k === 'registration' ||
      l.includes('number plate') ||
      l.includes('registration')
    );
  });
  return hit?.key || null;
}

const FieldRow = React.memo(function FieldRow({
  f,
  isRequired,
  value,
  setField,
  fieldInputType,
  mileageKey,
  plateKey,
  plates,
  loadingPlates,
}) {
  const t = fieldInputType(f);

  // Special-case: plate/registration dropdown
  if (plateKey && f.key === plateKey) {
    return (
      <div className="field">
        <label className="input-label">
          {f.label || 'Vehicle Number Plate'}{isRequired && <span className="req">*</span>}
        </label>
        <select
          className="input"
          value={value}
          onChange={(e) => setField(plateKey, e.target.value)}
          disabled={loadingPlates}
        >
          <option value="">{loadingPlates ? 'Loading vehicles' : 'Select a vehicle'}</option>
          {plates.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>
    );
  }

  if (t === 'textarea') {
    return (
      <div className="field span-all">
        <label className="input-label">
          {f.label}{isRequired && <span className="req">*</span>}
        </label>
        <textarea
          className="input"
          rows={f.rows || 3}
          value={value}
          placeholder={f.placeholder || ''}
          onChange={(e) => setField(f.key, e.target.value)}
        />
      </div>
    );
  }

  if (t === 'select') {
    return (
      <div className="field">
        <label className="input-label">
          {f.label}{isRequired && <span className="req">*</span>}
        </label>
        <select
          className="input"
          value={value}
          onChange={(e) => setField(f.key, e.target.value)}
        >
          <option value="">Select</option>
          {(f.options || []).map(opt =>
            typeof opt === 'string'
              ? <option key={opt} value={opt}>{opt}</option>
              : <option key={opt.value} value={opt.value}>{opt.label}</option>
          )}
        </select>
      </div>
    );
  }

  return (
    <div className="field">
      <label className="input-label">
        {f.label}{isRequired && <span className="req">*</span>}
      </label>
      <input
        className="input"
        type={t === 'number' ? 'number' : t === 'date' ? 'date' : 'text'}
        value={value}
        placeholder={f.placeholder || ''}
        onChange={(e) => setField(f.key, e.target.value)}
      />
      {String(f.key) === mileageKey && (
        <div className="hint">Enter the current odometer reading (required).</div>
      )}
    </div>
  );
});

export default function FleetChecks() {
  const { api } = useAuth();
  const cmd = useCommands();

  const [formType, setFormType] = useState('vehicleCheckGPM');
  const schema = useMemo(
    () => FLEET_CHECK_SCHEMAS[formType] || FLEET_CHECK_SCHEMAS.vehicleCheckGPM,
    [formType]
  );

  const [formData, setFormData] = useState({});
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [plates, setPlates] = useState([]);               // [{value, label}]
  const [loadingPlates, setLoadingPlates] = useState(true);
  const plateKey = useMemo(() => detectPlateKey(schema), [schema]);
  // add just below: const [photos, setPhotos] = useState([]);
  const [costGBP, setCostGBP] = useState('0'); // keep as string for the input; send as number



  // Load vehicles => plate dropdown
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingPlates(true);
        const list = await api.get('/fleet/vehicles').then(r => r.data).catch(() => []);
        if (!alive) return;
        const opts = (Array.isArray(list) ? list : [])
          .map(v => ({
            value: v.reg || v.numberPlate || '',
            label: [v.reg || v.numberPlate, v.make, v.model].filter(Boolean).join(' — ')
          }))
          .filter(o => o.value)
          .sort((a, b) => a.value.localeCompare(b.value));
        setPlates(opts);
      } finally {
        if (alive) setLoadingPlates(false);
      }
    })();
    return () => { alive = false; };
  }, [api]);

  const requiredSet = useMemo(() => new Set(schema.required || []), [schema]);
  const mileageKey = useMemo(() => detectMileageKey(schema), [schema]);

  // Reset form when schema changes
  useEffect(() => {
    setFormData({});
    setPhotos([]);
    setError('');
    setSuccess('');
    setCostGBP('0');
  }, [schema]);

  function setField(key, value) {
    setFormData(prev => ({ ...prev, [key]: value }));
  }

  async function onPickPhotos(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const uris = await Promise.all(files.map(fileToDataUri));
    setPhotos(prev => [...prev, ...uris]);
    e.target.value = ''; // reset input
  }
async function onSubmit() {
  setError('');
  setSuccess('');

  // ---- required fields per schema
  const missing = (schema.required || []).filter(k => {
    const v = formData[k];
    return v === undefined || v === null || String(v).trim() === '';
  });
  if (missing.length) {
    setError(`Please fill the required fields: ${missing.join(', ')}`);
    return;
  }

  // ---- detect + validate special fields
  if (!mileageKey) {
    setError('This form has no mileage field defined. Please update the schema.');
    return;
  }
  const reg = plateKey ? String(formData[plateKey] || '').trim().toUpperCase() : '';
  const odometerRaw = formData[mileageKey];
  const odometer = Number(odometerRaw);

  if (!reg) {
    setError('Vehicle number plate is required.');
    return;
  }
  if (!Number.isFinite(odometer) || odometer < 0) {
    setError('Mileage (odometer) must be a valid non-negative number.');
    return;
  }

  // normalise date -> YYYY-MM-DD; use schema field if present, else today
  const isoToday = new Date().toISOString().slice(0, 10);
  const date =
    formData.Date?.slice(0, 10) ||
    formData.date?.slice(0, 10) ||
    isoToday;

  // ---- build items (everything except the lifted keys)
  const SPECIAL = new Set(['Date', 'date', 'Notes', 'notes', plateKey, mileageKey].filter(Boolean));
  const items = {};
  for (const f of (schema.fields || [])) {
    const k = f.key;
    if (!k || SPECIAL.has(k)) continue;
    items[k] = formData[k] ?? '';
  }

  // notes (support either Notes / notes)
  const notes = (formData.Notes ?? formData.notes ?? '').toString();

  try {
    setLoading(true);
    // Normalise cost (non-negative number). Default 0.
const parsedCost = Number(costGBP);
if (!Number.isFinite(parsedCost) || parsedCost < 0) {
  setError('Repair/Expense (£) must be a non-negative number.');
  return;
}
const normalizedCost = Math.round(parsedCost * 100) / 100;


    // POST exactly what your server expects
    await api.post('/fleet/checks', {
      reg,
      date,
      odometer,
      items,
      notes,
      photos, // [{name, dataUri}] already in that shape
      costGBP: normalizedCost, // new field
    });

    setSuccess('Vehicle check submitted.');
    setFormData({});
    setPhotos([]);
  } catch (e) {
    // Surface server’s messages like "Missing reg or date" / "Mileage (odometer) is required."
    setError(e?.response?.data?.error || e?.message || 'Submit failed');
  } finally {
    setLoading(false);
  }
}

  return (
    <div className="container">
      <PageHeader
        eyebrow="Fleet"
        title="Vehicle check"
        actions={
          <>
            <select
              className="input"
              style={{ width: 280 }}
              aria-label="Check type"
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
            >
              {FORM_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button className="btn" onClick={onSubmit} disabled={loading}>
              {loading ? 'Submitting' : 'Submit check'}
            </button>
          </>
        }
      />

      {(error || success) && (
        <div className="page-alerts">
          {error && <div className="alert error">{error}</div>}
          {success && <div className="alert success">{success}</div>}
        </div>
      )}

      {/* Dynamic Form */}
      <div className="card">
        <div className="card-head">
          <div>
            <h2 className="card-title">{FORM_OPTIONS.find(o => o.value === formType)?.label || schema.name}</h2>
            {schema.description && <p className="card-sub">{schema.description}</p>}
          </div>
        </div>

        <div className="form-grid cols-2">
          {(schema.fields || []).map((f) => (
            <FieldRow
              key={f.key}
              f={f}
              isRequired={requiredSet.has(f.key)}
              value={formData[f.key] ?? ''}
              setField={setField}
              fieldInputType={fieldInputType}
              mileageKey={mileageKey}
              plateKey={plateKey}
              plates={plates}
              loadingPlates={loadingPlates}
            />
          ))}

          {/* Notes if schema did not include it */}
          {schema.fields?.every(f => f.key !== 'Notes') && (
            <div className="field span-all">
              <label className="input-label">Notes</label>
              <textarea
                className="input"
                rows={3}
                value={formData.Notes || ''}
                placeholder="Any other comments"
                onChange={(e) => setField('Notes', e.target.value)}
              />
            </div>
          )}

          {/* Photos (optional) */}
          {schema.photos && (
            <div className="field">
              <label className="input-label">Photos</label>
              <input
                className="input"
                type="file"
                accept="image/*"
                multiple
                onChange={onPickPhotos}
              />
              {photos.length > 0 && (
                <div className="preview">
                  <span className="muted">{photos.length} photo{photos.length === 1 ? '' : 's'} selected</span>
                  <button className="ghost" onClick={() => setPhotos([])}>Clear</button>
                </div>
              )}
            </div>
          )}

          <div className="field">
            <label className="input-label">Repair or expense</label>
            <div className="input-wrap"><span className="prefix">£</span>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={costGBP}
                onChange={(e) => setCostGBP(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="hint">Leave at 0 if there was no repair or expense.</div>
          </div>
        </div>

        <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
          <button className="btn secondary" onClick={() => { setFormData({}); setPhotos([]); }}>
            Reset
          </button>
          <button className="btn" onClick={onSubmit} disabled={loading}>
            {loading ? 'Submitting' : 'Submit check'}
          </button>
        </div>
      </div>
    </div>
  );
}
